import type { CSSProperties, ReactNode } from 'react';
import type { SheetMirrorColumnDto } from '@bhops/shared';
import { Icon } from '@/components/Icon';

// 시트 거울 표의 칸 모양(2026-10-23) — 오너: 칸은 시트처럼 최대한 좁게, 내용은 가운데.
//   · 머리글은 시트 원문 그대로 줄바꿈('IB↵날짜')
//   · 칸 너비는 처음엔 화면이 정해 둔 기본 폭(widths), 없으면 시트의 열 너비(스크립트가 읽어 온다).
//     넘치는 글자는 '…' — 마우스를 올리면 전체.
//   · 콘솔 공용 '칸 너비 끌어 바꾸기'(lib/table-column-resize)는 그대로 된다 — 두 번 누르면 시트 너비로.
//     머리글의 짧은 구분선 대신 칸 사이 선을 시트처럼 칸 높이 그대로 긋는다(오너 "이건 넣어야지").
//   · 색(오너 "시트 보면 이렇게 구분감 있게 해뒀던데"): 칸 바탕색은 시트에서 그대로 읽어 온다.
//     머리글 색은 따라가지 않는다(오너 "헤더 색까지 따라갈 필요는 없어") — 콘솔 머리글 그대로.
//     드롭다운 칩 색은 구글이 스크립트에 열어 주지 않아 화면이 정해 둔다(SheetChipColors) — 없는 값은 회색.

/** 시트에 너비가 없을 때(옛 스크립트 · 로컬 흉내) */
const FALLBACK_WIDTH = 90;
const MAX_WIDTH = 480;
/** 칸 좌우 여백 + 선 */
const CELL_EXTRA = 9;

/** 칸 열쇠 → 콘솔 기본 폭(px, 여백 · 선 포함) — 화면이 시트 열 너비 대신 정해 둔 폭. */
export type SheetWidths = Readonly<Record<string, number>>;

export function colWidth(c: SheetMirrorColumnDto, preset?: SheetWidths): number {
  return preset?.[c.key] ?? Math.min(c.width ?? FALLBACK_WIDTH, MAX_WIDTH) + CELL_EXTRA;
}

/**
 * 시트 모양 표 — 고정 배치(table-layout: fixed)로 칸 너비를 머리글 칸이 정한다.
 * data-sheet-table: 공용 폭 조절이 '처음 너비'를 머리글 칸의 data-sheet-width 에서 되찾는다.
 */
export function SheetTable({
  columns,
  widths,
  children,
}: {
  columns: SheetMirrorColumnDto[];
  widths?: SheetWidths;
  children: ReactNode;
}) {
  const total = columns.reduce((s, c) => s + colWidth(c, widths), 0);
  return (
    <table data-sheet-table="" style={{ ...table, width: total, minWidth: total }}>
      {children}
    </table>
  );
}

/**
 * 머리글 칸 — 시트 머리글 원문(줄바꿈 그대로).
 * onSort 가 있으면 누를 때마다 오름 → 내림 → 원래 순서(2026-10-23 오너 — 콘솔에서만 정렬, 시트는 그대로).
 */
export function SheetTh({
  col,
  widths,
  sort,
  onSort,
  baseBg,
}: {
  col: SheetMirrorColumnDto;
  widths?: SheetWidths;
  sort?: SheetSort;
  onSort?: (next: SheetSort) => void;
  /** 표에서 가장 흔한 시트 머리글 색 — 이 색인 칸은 콘솔 기본 머리글 그대로(headerBaseBg) */
  baseBg?: string;
}) {
  const w = colWidth(col, widths);
  const dir = sort?.key === col.key ? sort.dir : null;
  const tint = col.headerBg && col.headerBg !== baseBg ? col.headerBg : undefined;
  return (
    <th
      data-head-tint={tint ? '' : undefined}
      style={{
        ...th,
        width: w,
        minWidth: w,
        maxWidth: w,
        ...(onSort ? { cursor: 'pointer', userSelect: 'none' } : null),
        // 시트 머리글 칸 색으로 묶음 구분(2026-10-23 오너 "아래 표도 원본 시트처럼 색 구분" →
        // "머리글 칸 색은 콘솔 테마에 맞추면 된다") — 시트 색을 테마 머리글 바탕에 옅게 섞는다.
        // → "8행 보면 … 색 나눠놨잖아. 그런거처럼 구분 잘 되게" — 위쪽에 시트 색 띠를 더한다.
        // 바탕은 globals.css 의 th[data-head-tint] 가 섹션 틴트에 섞는다(섹션 머리글 색이 !important 라서).
        ...(tint ? ({ '--sheet-head-tint': tint } as CSSProperties) : null),
        ...(dir ? { color: 'var(--brand)' } : null),
      }}
      title={onSort ? `${col.key}: 누르면 콘솔에서만 정렬합니다` : col.key}
      data-sheet-width={w}
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined}
      onClick={onSort ? () => onSort(nextSort(sort ?? null, col.key)) : undefined}
    >
      {col.header.trim()}
      {dir && (
        <span style={sortMark} aria-hidden>
          <Icon name={dir === 'asc' ? 'chev-up' : 'chev-down'} size={10} />
        </span>
      )}
    </th>
  );
}

/** 콘솔 화면에서만의 정렬 — 칸 열쇠와 방향. null = 시트 순서 그대로. */
export type SheetSort = { key: string; dir: 'asc' | 'desc' } | null;

/** 같은 칸을 누를 때마다 오름 → 내림 → 풀기, 다른 칸이면 오름부터. */
export function nextSort(cur: SheetSort, key: string): SheetSort {
  if (cur?.key !== key) return { key, dir: 'asc' };
  return cur.dir === 'asc' ? { key, dir: 'desc' } : null;
}

const collator = new Intl.Collator('ko', { numeric: true, sensitivity: 'base' });

/**
 * 줄 정렬 — 실제 값(날짜 'YYYY-MM-DD' · 숫자)이 있으면 그것으로, 없으면 보이는 글자로('10. 2' < '10. 12').
 * 빈 칸은 방향과 상관없이 맨 끝. 같은 값끼리는 원래 순서.
 */
export function sortSheetRows<
  T extends { cells: Record<string, string>; raw?: Record<string, string | number> },
>(rows: readonly T[], sort: SheetSort): T[] {
  if (!sort) return [...rows];
  const val = (r: T) => {
    const raw = r.raw?.[sort.key];
    if (raw !== undefined && raw !== '') return raw;
    return (r.cells[sort.key] ?? '').trim();
  };
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i, v: val(r) }))
    .sort((a, b) => {
      const ae = a.v === '';
      const be = b.v === '';
      if (ae || be) return ae === be ? a.i - b.i : ae ? 1 : -1;
      const d =
        typeof a.v === 'number' && typeof b.v === 'number'
          ? a.v - b.v
          : collator.compare(String(a.v), String(b.v));
      return d ? d * sign : a.i - b.i;
    })
    .map((x) => x.r);
}

/**
 * 칸 열쇠 → (값 → [바탕, 글자]) — 시트 드롭다운 칩 색. 값 끝이 '*' 면 앞부분이 같은 값 모두('2-*').
 * 구글 시트 칩 기본 색표: 옅은 색(바탕) · 짙은 색(글자) 짝.
 */
export type SheetChipColors = Readonly<
  Record<string, Readonly<Record<string, readonly [string, string]>>>
>;

const NEUTRAL_CHIP = ['#e6e6e6', '#3d3d3d'] as const;

/** 칩 색 — 화면이 정해 둔 색이 없으면 회색. */
export function chipColorOf(
  palette: Readonly<Record<string, readonly [string, string]>> | undefined,
  v: string,
): readonly [string, string] {
  if (!palette) return NEUTRAL_CHIP;
  if (palette[v]) return palette[v]!;
  const prefix = Object.keys(palette).find((k) => k.endsWith('*') && v.startsWith(k.slice(0, -1)));
  return prefix ? palette[prefix]! : NEUTRAL_CHIP;
}

/** 가장 많은 열이 쓰는 시트 머리글 색 — 그 색은 '기본'으로 보고 테마 머리글 그대로 둔다. */
export function headerBaseBg(columns: readonly SheetMirrorColumnDto[]): string | undefined {
  const n = new Map<string, number>();
  for (const c of columns) if (c.headerBg) n.set(c.headerBg, (n.get(c.headerBg) ?? 0) + 1);
  let best: string | undefined;
  for (const [k, v] of n) if (!best || v > n.get(best)!) best = k;
  return best;
}

/** 칩으로 그릴 칸이면 칩(여러 개 고르기는 칩 여럿), 아니면 글자 그대로. */
export function SheetValue({
  col,
  value,
  colors,
}: {
  col: SheetMirrorColumnDto;
  value: string;
  colors?: SheetChipColors;
}) {
  const palette = colors?.[col.key];
  if (!palette || !value.trim()) return <>{value}</>;
  const parts = col.multi
    ? value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [value.trim()];
  return (
    <span style={chipRow}>
      {parts.map((p) => {
        const [bg, fg] = chipColorOf(palette, p);
        return (
          <span key={p} style={{ ...chip, background: bg, color: fg }}>
            {p}
          </span>
        );
      })}
    </span>
  );
}

/** 내용 칸 — 칸 너비로 자르고 가운데. long 이면 왼쪽(비고 같은 긴 글). */
export function SheetTd({
  children,
  title,
  long,
  bg,
  style,
}: {
  children: ReactNode;
  title?: string;
  long?: boolean;
  /** 시트 칸 바탕색 */
  bg?: string;
  style?: CSSProperties;
}) {
  return (
    <td style={{ ...sheetTd, ...(bg ? { background: bg } : null), ...style }} title={title}>
      <div style={{ ...sheetClip, textAlign: long ? 'left' : 'center' }}>{children}</div>
    </td>
  );
}

export const sheetTableWrap: CSSProperties = {
  // 상자는 표 폭만큼(칸 폭 합이 화면보다 좁으면 오른쪽에 빈 상자가 남아 잘린 듯 보였다 — 2026-10-23 오너
  // "왜 옆이 잘려있니"). 넓은 표는 화면 폭까지 · 가로 스크롤.
  width: 'fit-content',
  maxWidth: '100%',
  overflow: 'auto',
  maxHeight: 'calc(100vh - 320px)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};
const table: CSSProperties = {
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  fontSize: 12,
};
const th: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: 'var(--bg-sunken)',
  padding: '4px 4px',
  textAlign: 'center',
  verticalAlign: 'middle',
  whiteSpace: 'pre-line',
  lineHeight: 1.25,
  overflow: 'hidden',
  boxSizing: 'border-box',
  borderBottom: '1px solid var(--border-subtle)',
  borderRight: '1px solid var(--border-subtle)',
  fontWeight: 600,
};
const sortMark: CSSProperties = {
  position: 'absolute',
  right: 2,
  bottom: 2,
  display: 'inline-flex',
};
export const sheetTd: CSSProperties = {
  padding: '5px 4px',
  borderBottom: '1px solid var(--border-subtle)',
  borderRight: '1px solid var(--border-subtle)',
  verticalAlign: 'middle',
};
const chipRow: CSSProperties = { display: 'inline-flex', gap: 3, maxWidth: '100%' };
const chip: CSSProperties = {
  display: 'inline-block',
  padding: '0 8px',
  borderRadius: 999,
  fontSize: 11,
  lineHeight: '18px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
export const sheetClip: CSSProperties = {
  // 빈 칸만 있는 줄(새 줄 작성 중)도 다른 줄과 같은 높이(2026-10-23 오너 "쥐콩만하게")
  minHeight: 18,
  lineHeight: '18px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
